import { notifyError } from "@/lib/notify-error";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

interface CustomTrackerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreateTracker: (name: string) => void | Promise<void>;
}

export function CustomTrackerDialog({
  open,
  onOpenChange,
  onCreateTracker,
}: CustomTrackerDialogProps) {
  const [newTrackerName, setNewTrackerName] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const handleOpenChange = (nextOpen: boolean) => {
    onOpenChange(nextOpen);
    if (!nextOpen) {
      setNewTrackerName("");
    }
  };

  const handleCreate = async () => {
    const trimmedName = newTrackerName.trim();
    if (!trimmedName || isCreating) {
      return;
    }

    setIsCreating(true);
    try {
      await onCreateTracker(trimmedName);
      handleOpenChange(false);
    } catch (caughtError) {
      notifyError(caughtError, "Failed to create custom tracker. Please try again.");
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Custom Tracker</DialogTitle>
          <DialogDescription>
            Enter a name for your custom tracker. You can then add items to it from the submittal table.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <Input
            placeholder="Tracker name (e.g., Fire Protection)"
            value={newTrackerName}
            onChange={(event) => setNewTrackerName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && newTrackerName.trim() && !isCreating) {
                event.preventDefault();
                void handleCreate();
              }
            }}
          />
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isCreating}
          >
            Cancel
          </Button>
          <Button
            onClick={() => void handleCreate()}
            disabled={isCreating || !newTrackerName.trim()}
          >
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
